import clsx from 'clsx';
import { Bot, User, ArrowRight } from 'lucide-react';

import { Markdown } from './Markdown.jsx';
import { ChatWidgets } from './ChatWidgets.jsx';

/**
 * Hide the follow-up actions block during streaming. The model emits it at the
 * very end as `<<<ACTIONS>>> ... <<<END>>>`; the server strips it from the final
 * reply, but raw stream chunks still contain it, so we cut at the first marker
 * for display. Output never legitimately contains `<<<`.
 */
function visibleText(content) {
  if (typeof content !== 'string') return content;
  const i = content.indexOf('<<<');
  return i === -1 ? content : content.slice(0, i).replace(/\s+$/, '');
}

/**
 * A single chat turn. Supports incremental text growth during streaming and
 * an optional "streaming" caret. Assistant turns render Markdown; user turns
 * stay plain text. Assistant turns may carry follow-up `suggestions`, rendered
 * as one-click buttons that re-send the suggestion via `onSuggestion`.
 */
export function MessageBubble({ role, content, streaming, suggestions, widgets, onSuggestion, customerSessionId }) {
  const isUser = role === 'user';
  const actions = !isUser && !streaming && Array.isArray(suggestions) ? suggestions : [];
  return (
    <div className={clsx('flex gap-3', isUser ? 'flex-row-reverse' : 'flex-row')}>
      <div className={clsx(
        'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
        isUser ? 'bg-ink text-white' : 'bg-accent-soft text-accent'
      )}>
        {isUser ? <User className="h-4 w-4" strokeWidth={1.75} /> : <Bot className="h-4 w-4" strokeWidth={1.75} />}
      </div>
      <div className={clsx('flex min-w-0 flex-col gap-2', isUser ? 'items-end' : 'items-start')}>
        <div className={clsx(
          'max-w-full rounded-2xl px-4 py-2.5 text-sm',
          isUser ? 'rounded-tr-sm bg-ink text-white' : 'rounded-tl-sm border border-border bg-surface-elevated text-ink'
        )}>
          {isUser ? (
            <p className="whitespace-pre-wrap break-words">{content}</p>
          ) : (
            <>
              <Markdown>{visibleText(content)}</Markdown>
              {streaming && <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse rounded-sm bg-current align-middle" />}
              {!streaming && <ChatWidgets widgets={widgets} customerSessionId={customerSessionId} />}
            </>
          )}
        </div>

        {actions.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {actions.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onSuggestion && onSuggestion(s)}
                className="inline-flex items-center gap-1.5 rounded-full border border-accent/30 bg-accent-soft px-3 py-1.5 text-xs font-medium text-accent transition hover:bg-accent/15"
              >
                {s}
                <ArrowRight className="h-3 w-3" strokeWidth={2} />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
